import React, { useState, useEffect, useId, useMemo, useRef } from "react";
import { FiChevronDown, FiX } from "react-icons/fi";

/* ── Design tokens ── */
export const T = {
    radius: "12px",
    radiusSm: "8px",
    border: "#e8edf3",
    bg: "#f7f9fc",
    cardBg: "#ffffff",
    textPrimary: "#0f172a",
    textMuted: "#64748b",
    textLight: "#94a3b8",
    accent: "#2563eb",
    success: "#059669",
    danger: "#dc2626",
    shadow: "0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)",
};

export const firstChar = (s = "") => (s.trim()[0] ?? "?").toUpperCase();

/* ── StatusBadge ── */
export function StatusBadge({ value = "Active" }) {
    const active = String(value).toLowerCase() === "active";
    return (
        <span style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            background: active ? "#ecfdf5" : "#fef2f2",
            color: active ? T.success : T.danger,
            border: `1px solid ${active ? "#a7f3d0" : "#fecaca"}`,
            borderRadius: 999, padding: "3px 10px",
            fontSize: 12, fontWeight: 600,
        }}>
            <span style={{
                width: 6, height: 6, borderRadius: "50%",
                background: active ? T.success : T.danger,
            }} />
            {active ? "Active" : "Inactive"}
        </span>
    );
}

/* ── Avatar ── */
export function Avatar({ name, size = 56 }) {
    return (
        <div style={{
            width: size, height: size, borderRadius: "50%",
            border: `2px solid ${T.border}`, background: "#eef2f7",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 0 0 4px #f0f4ff", flexShrink: 0,
        }}>
            <span style={{ fontSize: size * 0.38, fontWeight: 700, color: "#4f6a8a" }}>
                {firstChar(name)}
            </span>
        </div>
    );
}

/* ── SectionHeading ── */
export function SectionHeading({ icon: Icon, title }) {
    return (
        <div style={{
            display: "flex", alignItems: "center", gap: 8,
            paddingBottom: 10, marginBottom: 14,
            borderBottom: `1px solid ${T.border}`,
        }}>
            <div style={{
                width: 24, height: 24, borderRadius: "6px",
                background: "#eff6ff", color: T.accent,
                display: "flex", alignItems: "center", justifyContent: "center",
            }}>
                <Icon size={12} />
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary }}>
                {title}
            </span>
        </div>
    );
}

/* ── InfoRow (view mode) ── */
export function InfoRow({ icon: Icon, label, children }) {
    return (
        <div style={{
            display: "flex", alignItems: "flex-start", gap: 12,
            padding: "10px 0", borderBottom: `1px solid ${T.bg}`,
        }}>
            {Icon && (
                <div style={{
                    width: 28, height: 28, borderRadius: "7px",
                    background: T.bg, color: T.textMuted, flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                    <Icon size={13} />
                </div>
            )}
            <div>
                <div style={{ fontSize: 11, color: T.textLight, fontWeight: 500, marginBottom: 2 }}>
                    {label}
                </div>
                <div style={{ fontSize: 14, color: T.textPrimary, fontWeight: 500 }}>
                    {children || "—"}
                </div>
            </div>
        </div>
    );
}

/* ── FieldInput (edit mode) ── */
export function FieldInput({
    label, name, value, onChange,
    type = "text", as, rows,
    placeholder, required, error,
    onBlur = null,
}) {
    const generatedId = useId();
    const inputId = `${name || "field"}-${generatedId.replace(/:/g, "")}`;
    const [focused, setFocused] = useState(false);
    const borderColor = error ? T.danger : focused ? T.accent : T.border;
    const base = {
        width: "100%", fontSize: 13, color: T.textPrimary,
        background: "#fff", fontFamily: "inherit",
        border: `1.5px solid ${borderColor}`,
        borderRadius: T.radiusSm, outline: "none",
        boxShadow: focused && !error ? "0 0 0 3px rgba(37,99,235,0.10)" : "none",
        transition: "border-color 0.15s, box-shadow 0.15s",
        boxSizing: "border-box",
    };
    const shared = {
        id: inputId, name, value: value ?? "", onChange, placeholder,
        onFocus: () => setFocused(true),
        onBlur: () => {
            setFocused(false);
            if (onBlur) onBlur();
        },
    };
    return (
        <div style={{ marginBottom: 14 }}>
            <label htmlFor={inputId} style={{
                display: "block", fontSize: 11,
                fontWeight: 600, color: T.textMuted, marginBottom: 5,
            }}>
                {label}
                {required && <span style={{ color: T.danger }}> *</span>}
            </label>
            {as === "textarea"
                ? <textarea rows={rows ?? 3} {...shared}
                    style={{ ...base, padding: "9px 12px", resize: "vertical", display: "block" }} />
                : <input type={type} {...shared}
                    style={{ ...base, height: 36, padding: "0 11px", display: "block" }} />
            }
            {error && <div style={{ fontSize: 11, color: T.danger, marginTop: 4 }}>{error}</div>}
        </div>
    );
}

/* ── SearchableSelect with animated chevron ── */
export function SearchableSelect({
    label, name, value, onChange,
    options = [], placeholder, required, error,
    allowFreeText = true,
    renderOption = null,
    renderValue = null,
}) {
    const generatedId = useId();
    const inputId = `${name || "select"}-${generatedId.replace(/:/g, "")}`;
    const listboxId = `${inputId}-options`;
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState(value || "");
    const [focused, setFocused] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const wrapRef = useRef(null);

    useEffect(() => { setQuery(value || ""); }, [value]);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target))
                setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    const filtered = useMemo(() => {
        const q = (query || "").toLowerCase();
        return q ? options.filter((o) => {
            const optStr = typeof o === 'string' ? o : String(o).toLowerCase();
            return optStr.toLowerCase().includes(q);
        }) : options;
    }, [query, options]);

    const borderColor = error ? T.danger : focused ? T.accent : T.border;

    const handleInput = (e) => {
        const v = e.target.value;
        setQuery(v);
        setOpen(true);
        setActiveIndex(-1);
        if (allowFreeText) onChange({ target: { name, value: v } });
    };

    const handleSelect = (opt) => {
        setQuery(opt);
        setOpen(false);
        setActiveIndex(-1);
        onChange({ target: { name, value: opt } });
    };

    const handleKeyDown = (event) => {
        if (event.key === "ArrowDown") {
            event.preventDefault();
            setOpen(true);
            setActiveIndex((current) => Math.min(current + 1, filtered.length - 1));
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setOpen(true);
            setActiveIndex((current) => current <= 0 ? filtered.length - 1 : current - 1);
        } else if (event.key === "Enter" && open && activeIndex >= 0) {
            event.preventDefault();
            handleSelect(filtered[activeIndex]);
        } else if (event.key === "Escape") {
            event.preventDefault();
            setOpen(false);
            setActiveIndex(-1);
        }
    };

    // Render the display text for the selected value
    const displayValue = renderValue ? renderValue(value) : value;

    return (
        <div style={{ marginBottom: 14, position: "relative" }} ref={wrapRef}>
            <label htmlFor={inputId} style={{
                display: "block", fontSize: 11,
                fontWeight: 600, color: T.textMuted, marginBottom: 5,
            }}>
                {label}
                {required && <span style={{ color: T.danger }}> *</span>}
            </label>
            <div style={{ position: "relative" }}>
                <input
                    id={inputId}
                    type="text" autoComplete="off"
                    role="combobox"
                    aria-autocomplete="list"
                    aria-expanded={open}
                    aria-controls={listboxId}
                    aria-activedescendant={
                        open && activeIndex >= 0 ? `${listboxId}-${activeIndex}` : undefined
                    }
                    value={query} placeholder={placeholder}
                    onChange={handleInput}
                    onFocus={() => { setFocused(true); setOpen(true); setActiveIndex(-1); }}
                    onBlur={() => setFocused(false)}
                    onKeyDown={handleKeyDown}
                    style={{
                        width: "100%", height: 36, padding: "0 32px 0 11px",
                        fontSize: 13, color: T.textPrimary,
                        background: "#fff", fontFamily: "inherit",
                        border: `1.5px solid ${borderColor}`,
                        borderRadius: T.radiusSm, outline: "none",
                        boxSizing: "border-box",
                        boxShadow: focused && !error
                            ? "0 0 0 3px rgba(37,99,235,0.10)" : "none",
                        transition: "border-color 0.15s, box-shadow 0.15s",
                    }}
                />
                <span
                    onClick={() => setOpen((o) => !o)}
                    style={{
                        position: "absolute", right: 9, top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer", display: "flex", alignItems: "center",
                    }}
                >
                    <FiChevronDown
                        size={14}
                        style={{
                            transition: "transform 0.22s ease, color 0.15s",
                            transform: open ? "rotate(180deg)" : "rotate(0deg)",
                            color: open ? T.accent : T.textLight,
                        }}
                    />
                </span>
            </div>

            {error && <div style={{ fontSize: 11, color: T.danger, marginTop: 4 }}>{error}</div>}

            {open && filtered.length > 0 && (
                <div
                    id={listboxId}
                    role="listbox"
                    aria-label={`${label} options`}
                    style={{
                    position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
                    background: "#fff", border: `1px solid ${T.border}`,
                    borderRadius: T.radiusSm,
                    boxShadow: "0 8px 24px -4px rgba(0,0,0,0.12)",
                    zIndex: 2000, maxHeight: 220, overflowY: "auto",
                    animation: "cd-dropdown-in 0.15s ease",
                }}>
                    <style>{`
                        @keyframes cd-dropdown-in {
                            from { opacity: 0; transform: translateY(-6px); }
                            to   { opacity: 1; transform: translateY(0);    }
                        }
                    `}</style>
                    {filtered.map((opt) => {
                        const isSelected = opt === value;
                        const displayText = renderOption ? renderOption(opt) : opt;

                        return (
                            <button
                                type="button"
                                id={`${listboxId}-${filtered.indexOf(opt)}`}
                                role="option"
                                aria-selected={isSelected}
                                key={opt}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => handleSelect(opt)}
                                onMouseEnter={() => setActiveIndex(filtered.indexOf(opt))}
                                style={{
                                    display: "block", width: "100%", border: 0, textAlign: "left",
                                    padding: "9px 12px", fontSize: 13, cursor: "pointer",
                                    color: isSelected ? T.accent : T.textPrimary,
                                    background: activeIndex === filtered.indexOf(opt)
                                        ? "#f8fafc"
                                        : isSelected ? "#eff6ff" : "transparent",
                                    fontWeight: isSelected ? 600 : 400,
                                }}
                            >
                                {displayText}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

/* ── Banner ── */
export function Banner({ type, msg, onClose }) {
    if (!msg) return null;
    const colors = {
        success: { bg: "#ecfdf5", color: "#065f46", border: "#a7f3d0" },
        danger: { bg: "#fef2f2", color: "#991b1b", border: "#fecaca" },
        info: { bg: "#eff6ff", color: "#1e40af", border: "#bfdbfe" },
    };
    const c = colors[type] ?? colors.info;
    return (
        <div role={type === "danger" ? "alert" : "status"} aria-live={type === "danger" ? "assertive" : "polite"} style={{
            padding: "11px 16px", borderRadius: T.radiusSm, marginBottom: 16,
            fontSize: 13, fontWeight: 500,
            background: c.bg, color: c.color, border: `1px solid ${c.border}`,
            display: "flex", alignItems: "center",
            justifyContent: "space-between", gap: 12,
        }}>
            <span>{msg}</span>
            <button onClick={onClose} aria-label="Dismiss notification" style={{
                background: "none", border: "none",
                cursor: "pointer", color: "inherit", padding: 0,
            }}>
                <FiX size={13} />
            </button>
        </div>
    );
}